import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const projects = [
  { name: "Legend", description: "The world's only audiovisual digital comic platform", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/652e67ead1e880d65ca31b2b_Legend.jpg", url: "https://legendhq.com/", category: "Media Tech", layout: "featured", alt: "Legend - audiovisual digital comic platform" },
  { name: "Challonge", description: "The leading esports tournament platform, acquired by Logitech", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/653263d26948a37ff69e4414_Challonge%20(1).jpg", url: "https://challonge.com/", category: "Esports", layout: "wide", acquired: true, alt: "Challonge - esports tournament platform" },
  { name: "XSplit", description: "Your production studio for live or pre-recorded content", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/652e6934d5f974cc9feaccc9_XSplit%20(1).jpg", url: "https://xsplit.com/", category: "Broadcasting", layout: "normal", alt: "XSplit - live streaming production" },
  { name: "Rejuven", description: "Digital health company focused on rescuing the human microbiome", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/652e6d6c7923bd13ba049011_Rejuven.jpg", url: "https://rejuvenhealth.com/", category: "HealthTech", layout: "normal", alt: "Rejuven - digital health platform" },
  { name: "Big 7 Media", description: "Digital media platform, acquired by EnjoyTravel.com", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/653263b5bde43d64e5dead34_Big7Media.jpg", url: "https://www.enjoytravel.com/us/travel-news", category: "Media", layout: "wide", acquired: true, alt: "Big 7 Media - travel media platform" },
  { name: "Player.me", description: "Social discovery for gamers, acquired by XSplit", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/653263df500e83c79128c888_Player.me%20(1).jpg", url: "https://xsplit.com/", category: "Social", layout: "normal", acquired: true, alt: "Player.me - social platform for gamers" },
  { name: "Canvid", description: "Next generation AI powered screen recording", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/65ddf5ff879e1f4bf50f8737_Canvid.jpg", url: "https://canvid.com/", category: "AI Tools", layout: "normal", alt: "Canvid - AI screen recording" },
  { name: "Videocom", description: "Video communication and collaboration platform", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/652e7f7e6f57cd62752d0cb7_Videocom%20(2).jpg", url: "https://videocom.com/", category: "Communication", layout: "wide", alt: "Videocom - video collaboration" },
  { name: "VCam", description: "Leading AI background segmentation app", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/652e80dfad528d57a1f18f49_VCam.jpg", url: "https://vcam.ai/", category: "AI Tech", layout: "normal", alt: "VCam - AI background removal" },
  { name: "Mist Media", description: "Production company for Film and TV", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/652e84a39790fb23e28b7a9b_Mist%20Media%20(1).jpg", url: "https://mistmedia.com/", category: "Production", layout: "normal", alt: "Mist Media - film production" },
  { name: "Big September", description: "Irish indie rock band from Bray", image: "https://cdn.prod.website-files.com/652d26aaad5aad4ee8f362ba/652f01cc366b2792eab635e8_Big%20September.jpg", url: "https://youtu.be/crT0xPQK2aU?si=lig-VQkBbWoSp2pz", category: "Music", layout: "normal", alt: "Big September - indie rock band" },
];

const cardVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

function ProjectCard({ project, index }) {
  const isWide = project.layout === "wide";
  const isFeatured = project.layout === "featured";

  return (
    <motion.article
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={cardVariants}
      whileHover={{ y: -4, transition: { duration: 0.3 } }}
      className={`project-card group relative overflow-hidden rounded-2xl bg-[#0A0A0A] border border-white/[0.04] neon-hover ${
        isFeatured ? "bento-featured md:row-span-2" : ""
      } ${isWide ? "bento-wide" : ""}`}
      data-testid={`project-card-${project.name.toLowerCase().replace(/[\s.]+/g, '-')}`}
    >
      <a href={project.url} target="_blank" rel="noopener noreferrer" className="block w-full h-full cursor-pointer" aria-label={`${project.name} — ${project.description}`} title={`Visit ${project.name}`}>
        <img src={project.image} alt={project.alt} className="project-card-img absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-25 transition-all duration-700" loading="lazy" width={600} height={400} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/20 z-10" aria-hidden="true" />

        {/* Top badges */}
        <div className="absolute top-5 left-5 right-5 z-20 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="liquid-glass rounded-full text-[9px] uppercase tracking-[0.2em] text-white/70 px-3 py-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>
              {project.category}
            </span>
            {project.acquired && (
              <span className="liquid-glass rounded-full text-[9px] uppercase tracking-[0.2em] text-amber-400/80 px-3 py-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>
                Acquired
              </span>
            )}
          </div>
          <span className="text-[10px] text-white/15" style={{ fontFamily: "system-ui, sans-serif" }}>
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 w-full p-6 z-20">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h3 className="text-lg md:text-xl text-white group-hover:text-white/80 transition-colors duration-300 italic">
                {project.name}
              </h3>
              <p className="text-[13px] text-white/35 leading-relaxed max-w-xs translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500" style={{ fontFamily: "system-ui, sans-serif" }}>
                {project.description}
              </p>
            </div>
            <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:border-white/30 flex-shrink-0 translate-x-2 group-hover:translate-x-0">
              <ArrowUpRight className="w-3.5 h-3.5 text-white/70" />
            </div>
          </div>
        </div>
      </a>
    </motion.article>
  );
}

export default function Projects() {
  return (
    <section id="projects" data-testid="projects-section" className="relative py-32 md:py-44 px-6 md:px-12 lg:px-24 overflow-hidden" aria-labelledby="projects-title">
      <div className="gradient-orb gradient-orb-1 bottom-[10%] right-[-5%]" aria-hidden="true" />
      <div className="section-divider mb-24" aria-hidden="true" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="flex items-center gap-4 mb-6">
              <div className="w-8 h-[1px] bg-white/20" />
              <p className="text-[11px] uppercase tracking-[0.3em] text-white/40" style={{ fontFamily: "system-ui, sans-serif" }} data-testid="projects-label">Selected Work</p>
            </motion.div>
            <motion.h2 id="projects-title" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.7 }} className="text-3xl md:text-5xl lg:text-6xl text-white italic" data-testid="projects-heading">
              Projects &amp; Ventures
            </motion.h2>
          </div>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }} className="text-xs text-white/20 uppercase tracking-wider" style={{ fontFamily: "system-ui, sans-serif" }}>
            {projects.length} Projects
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[260px] md:auto-rows-[280px]" role="list" aria-label="Portfolio of projects and ventures">
          {projects.map((project, i) => (
            <ProjectCard key={project.name} project={project} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
